/* 
  Developed by: Jordyn Kao
  Contact: jordyninternational@gmail.com
  This component is part of the application and contains internal credits.
  This code is protected under the terms of the contract agreement.
  Unauthorized use or distribution of this code is prohibited.
*/
import { useRef, useState, useEffect } from 'react';
import { LinkButton } from '@/components/Button';
import { TitleCaseH1, TitleCaseP, TitleCaseSpan } from './TitleCase';
import { Gem } from 'lucide-react';

const stats = [
  { value: '80%', label: 'of institutional portfolios in alternatives' },
  { value: '2-4%', label: 'higher annual returns vs. traditional' },
  { value: '0.3', label: 'correlation with stock markets' }
];

const HeroSection = () => {
  return (
    <section className="relative min-h-screen bg-gradient-to-b from-[hsl(var(--navy))] to-[hsl(var(--navy-dark))] text-white flex flex-col">
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/70 z-10"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20 flex-grow flex flex-col justify-center py-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-4 sm:mb-6">
            <TitleCaseSpan className="text-xs sm:text-sm font-medium text-white/90 tracking-wider">
              Alternative Investment Platform
            </TitleCaseSpan>
          </div>
          <TitleCaseH1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
            Beyond Stocks & Bonds:<br className="hidden sm:block" /> Alternative Investments
          </TitleCaseH1>
          <TitleCaseP className="text-lg sm:text-xl text-white/90 mb-6 sm:mb-8 max-w-3xl mx-auto">
            Diversify Your Portfolio With Private Market Investments That Were Once Only Available To Institutions. Access Higher Potential Returns With Lower Correlation To Traditional Markets.
          </TitleCaseP>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <LinkButton 
              to="/investments" 
              variant="solid"
              className="rounded-full px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              Explore Investments
            </LinkButton>
            <a 
              href="https://app.alta-x.com/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full px-8 py-4 text-lg font-semibold backdrop-blur-sm border-white/20 bg-white/5 hover:bg-white/10 transition-all duration-300 transform hover:-translate-y-1 inline-block"
              aria-label="Create an account"
            >
              Create an Account
            </a>
          </div>
          <div className="mt-8">
            <div className="flex items-center justify-center text-sm text-white/80 tracking-wide">
              <Gem className="w-4 h-4 mr-2.5 text-[hsl(var(--bronze-gold-light))]" />
              <TitleCaseSpan>Trusted By Over 1,500+ Investors In South Africa</TitleCaseSpan>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full bg-gradient-to-t from-black/50 to-transparent z-20 pt-10 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/20">
            {stats.map((stat, index) => (
              <div key={index} className="py-4 sm:py-6 px-4 text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-xs sm:text-sm text-white/70 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
